// Copyright (c) 2026 Finerty Data Ltd. All rights reserved.
//
// This file is part of proprietary software owned by Finerty Data Ltd.
// It is confidential and may not be copied, distributed, modified, or used,
// in whole or in part, without the prior written consent of Finerty Data Ltd.
//
// Author: Rafael Pereira Satyro

import { formatTime, formatDateTime } from '@loa/theme';
import type { ProfileRequestDTO } from '../../types/profileRequest';
import type { Policy } from '../../types/policy';

/** Relationship types whose client is a signing party on the policy. */
export const SIGNING_RELATIONSHIPS: ReadonlySet<string> = new Set([
  'SIGNATORY',
  'OWNER_AND_SIGNATORY',
]);

/** Title-case the ownership enum (SOLO → Solo) for the Type column. */
export function ownershipLabel(ownershipType: string): string {
  return ownershipType.charAt(0) + ownershipType.slice(1).toLowerCase();
}

/**
 * Resolve the signatory name(s) to display for a request. Prefers the
 * clients actually assigned to the request's SIGN actions; falls back to
 * the policy's signing-party signatories when the request doesn't enumerate
 * SIGN actions. Returns "—" when there's no policy or no resolvable name.
 */
export function signatoryNamesFor(
  request: ProfileRequestDTO,
  policy: Policy | null | undefined,
): string {
  const signatories = policy?.signatories;
  // Policy-less typed requests (New Business) carry the applicant name on the
  // request itself — there are no policy signatories to derive it from.
  if (!signatories || signatories.length === 0) {
    return request.requestSignatoryName || '—';
  }
  const signActorIds = new Set(
    request.actions
      .filter((a) => a.actionType === 'SIGN' && a.assignedActorRefId)
      .map((a) => a.assignedActorRefId as string),
  );
  let names = signatories
    .filter((s) => signActorIds.has(s.clientId))
    .map((s) => s.clientName);
  if (names.length === 0) {
    names = signatories
      .filter((s) => SIGNING_RELATIONSHIPS.has(s.relationshipType))
      .map((s) => s.clientName);
  }
  const unique = [...new Set(names.filter(Boolean))];
  return unique.length ? unique.join(', ') : '—';
}

/**
 * Format a "last updated" timestamp as "Today @ HH:mm" for today, else a
 * full date+time. Returns "—" for a missing/invalid value.
 */
export function formatLastUpdated(iso: string | null | undefined): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toDateString() === new Date().toDateString()
    ? `Today @ ${formatTime(iso)}`
    : formatDateTime(iso);
}

/**
 * The client id to nudge for a request — used to navigate to the resend
 * invitation page. Prefers the CLIENT-assigned SIGN action; falls back to
 * the first signing-party signatory on the policy. Null when unresolvable.
 */
export function clientIdForRequest(
  request: ProfileRequestDTO,
  policy: Policy | null | undefined,
): string | null {
  const signAction = request.actions.find(
    (a) => a.actionType === 'SIGN' && a.assignedActorType === 'CLIENT' && a.assignedActorRefId,
  );
  if (signAction?.assignedActorRefId) return signAction.assignedActorRefId;
  const sig = policy?.signatories?.find((s) => SIGNING_RELATIONSHIPS.has(s.relationshipType));
  return sig?.clientId ?? null;
}
